import React, { useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { Profile } from '@uncinc/react-drupal-core';

import splitListItems from '../../../helpers/splitListItems';
import ButtonWrapper from '../../ButtonWrapper';
import type { ProfileDefault } from '../../../types/uncinc_profile/profile';

import './style.scss';

const MAX_MEMBERS_SHOWN_BY_DEFAULT = 3;

export interface TeamWidgetProps {
  leads?: ProfileDefault[];
  members: ProfileDefault[];
  className?: string;
}

const TeamWidget: React.FC<TeamWidgetProps> = (props) => {
  const { leads, members, className } = props;

  const { t } = useTranslation('translation', { keyPrefix: 'components.widgets.team_widget' });

  const [isExpanded, setExpanded] = useState(false);
  const [initialMembers, additionalMembers] = useMemo(() => (
    splitListItems<ProfileDefault>(members, MAX_MEMBERS_SHOWN_BY_DEFAULT)
  ), [members]);

  const classes = c('TeamWidget', 'Paper', className, {
    'TeamWidget--expanded': isExpanded,
  });

  return (
    <div className={classes}>
      {(leads && leads.length > 0) && (
        <div className="TeamWidget__members">
          <h3 className="TeamWidget__members__title">
            {t('leads_title')}
          </h3>

          <ul className="TeamWidget__members__list">
            {leads.map((lead) => (
              <li key={`team-lead-${lead.profile_id}`}>
                <Profile entity={fromJS(lead)} viewMode="teaser" />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="TeamWidget__members">
        <h3 className="TeamWidget__members__title">
          {t(leads && leads.length > 0 ? 'members_title' : 'members_only_title')}
        </h3>

        {(initialMembers && initialMembers.length > 0) && (
          <ul className="TeamWidget__members__list TeamWidget__members__list--initial-group">
            {initialMembers.map((member) => (
              <li key={`team-member-${member.profile_id}`}>
                <Profile entity={fromJS(member)} viewMode="teaser" />
              </li>
            ))}
          </ul>
        )}

        {(additionalMembers && additionalMembers.length > 0) && (
          <>
            <div className="TeamWidget__members__list-wrapper">
              <ul className="TeamWidget__members__list TeamWidget__members__list--additional-group">
                {additionalMembers.map((member) => (
                  <li key={`team-member-${member.profile_id}`}>
                    <Profile entity={fromJS(member)} viewMode="teaser" />
                  </li>
                ))}
              </ul>
            </div>

            <ButtonWrapper
              className="TeamWidget__show-more-button"
              type="textual"
              iconName="keyboard_arrow_down"
              iconPosition="left"
              onClick={() => setExpanded(!isExpanded)}
            >
              {t(isExpanded ? 'members_show_less_button' : 'members_show_more_button')}
            </ButtonWrapper>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamWidget;
